import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  Page,
  Card,
  PageActions,
  Text,
  Loading,
  FormLayout,
  TextField,
  Toast,
  Modal,
  Select,
  Thumbnail,
  DropZone,
  InlineError,
  InlineStack,
  Button,
} from "@shopify/polaris";
import axios from "../../Assets/Lib/axios";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../../Components/LoadingScreen";
import JoditEditor from "jodit-react";
import { EditIcon, ImagesIcon } from "@shopify/polaris-icons";
import Select2 from "react-select";

const AddEditPlatform = ({ mode, url }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSaving, setIsSaving] = useState(mode === "edit");
  const editorConfig = useMemo(
    () => ({
      readonly: false,
      toolbarButtonSize: "middle",
      buttons: ["bold", "italic", "underline", "link", "fullsize", "source"],
      showXPathInStatusbar: false,
      showCharsCounter: false,
      showWordsCounter: false,
      showStatusbar: false,
      enableDragAndDropFileToEditor: false,
      spellcheck: false,
      askBeforePasteHTML: false,
      placeholder: "",
    }),
    []
  );
  const [item, setItem] = useState({
    platform_name: "",
    platform_logo: "",
    description: "",
    criteria: "",
    category: localStorage.getItem("benchmark-category") || "",
    images: [],
    country: "All",
    competitor: "",
  });

  const [errors, setErrors] = useState({});
  const [criteriaOptions, setCriteriaOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const handleChange = useCallback((field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  }, []);

  const [active, setActive] = useState(false);
  const toggleActive = useCallback(() => setActive((a) => !a), []);
  const toastMarkup = active ? (
    <Toast
      content="Changes have been saved successfully"
      onDismiss={toggleActive}
    />
  ) : null;

  const [activeDelete, setActiveDelete] = useState(false);
  const toggleDeleteModal = useCallback(() => {
    setActiveDelete((a) => !a);
    setIsSaving(false);
  }, []);

  const handleYesDelete = () => {
    setIsSaving(true);
    axios
      .delete(`/brand-benchmarks/${id}`)
      .then(() => {
        navigate(`/admin/brand-benchmark`);
      })
      .catch(console.error);
  };

  const validateFields = () => {
    const requiredFields = [
      // "platform_name",
      // "platform_logo",
      // "description",
      "criteria",
      "competitor",
      // "category",
    ];
    const newErrors = {};
    let hasError = false;

    requiredFields.forEach((field) => {
      if (!item[field] || item[field].toString().trim() === "") {
        hasError = true;
        newErrors[field] = "This field is required";
      }
    });
    if (!uploadedKeysRef.current.length && mode === "new") {
      hasError = true;
      newErrors["images"] = "Please upload at least one image";
    } else {
      setFileError("");
    }
    // if (!imageKey.current && mode === "new") {
    //   hasError = true;
    //   setFileError("Platform logo is required");
    // } else {
    //   setFileError("");
    // }

    setErrors(newErrors);
    return !hasError;
  };

  const [isImageUploading, setIsImageUploading] = useState(false);
  const validImageTypes = [
    "image/gif",
    "image/jpeg",
    "image/png",
    "image/webp",
  ];
  const [files, setFiles] = useState();
  const [fileError, setFileError] = useState("");
  const handleDropZone = useCallback(
    (_dropFiles, acceptedFiles, _rejectedFiles) => setFiles(acceptedFiles[0]),
    []
  );

  const fileUpload = !files && <DropZone.FileUpload />;
  const uploadedFiles = files && (
    <InlineStack gap="200" align="start" blockAlign="center">
      <div className="maxtextwidth">
        {files.name}{" "}
        <Text variant="bodySm" as="p">
          {files.size} bytes
        </Text>
      </div>
    </InlineStack>
  );
  const [imageError, setImageError] = useState("");
  const imageKey = useRef("");

  const sendImage = (files) => {
    setIsImageUploading(true);
    const form_data = new FormData();
    form_data.append("entity_type", "brand_benchmark_logo");
    form_data.append("image", files[files.length - 1]);

    axios
      .post(`/s3/save-file`, form_data)
      .then((result) => {
        imageKey.current = result.data.data.file_key;
        setIsImageUploading(false);
        setItem({
          ...item,
          platform_logo:
            validImageTypes.indexOf(files[files.length - 1].type) > -1
              ? window.URL.createObjectURL(files[files.length - 1])
              : "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png",
        });
        setImageError("");
      })
      .catch(() => {});
  };

  const [competitorsOptions, setCompetitorsOptions] = useState([]);

  useEffect(() => {
    axios.get("/brand-benchmark-criterias?all").then((res) => {
      console.log(res.data.data);
      setCriteriaOptions(
        res.data.data.map((c) => ({ label: c.title, value: String(c.id) }))
      );
    });

    axios.get("/brand-benchmark-categories").then((res) => {
      setCategoryOptions(
        res.data.data.data.map((c) => ({ label: c.title, value: String(c.id) }))
      );
    });
    axios.get("/competitors?all").then((res) => {
      setCompetitorsOptions(
        res.data.data.map((c) => ({
          label: c.business_name,
          value: String(c.id),
        }))
      );
    });

    if (mode === "edit") {
      axios
        .get(`/brand-benchmarks/${id}`)
        .then((result) => {
          const data = result?.data?.data || {};
          setItem({
            // platform_name: data?.platform_name || "",
            // platform_logo: data?.logo_url || "",
            description: data?.description || "",
            criteria: String(data?.criteria_id) || "",
            category: String(data?.category_id) || "",
            // country: data?.country || "",
            competitor: data?.competitor
              ? {
                  label: data?.competitor?.business_name,
                  value: String(data?.competitor?.id),
                }
              : "",
          });
          setIsSaving(false);
        })
        .catch((err) => console.log(err));
    }
  }, []);

  const [error, setError] = useState("");
  const [optionsCountries, setOptionsCountries] = useState([]);
  // useEffect(() => {
  //   axios
  //     .get("/settings/countries")
  //     .then((res) => {
  //       const countriesData = res?.data?.data || [];
  //       const options = [
  //         { label: "All", value: "All" },
  //         ...countriesData.map((country) => ({
  //           label: country.name,
  //           value: country.name,
  //         })),
  //       ];
  //       setOptionsCountries(options);
  //     })
  //     .catch((err) => console.log(err));
  // }, []);

  const [imageFiles, setImageFiles] = useState([]);
  const handleImageDrop = useCallback((_dropFiles, acceptedFiles) => {
    setImageFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const imageFileUpload = imageFiles.length === 0 && <DropZone.FileUpload />;

  const uploadedImageFiles =
    imageFiles.length > 0 &&
    imageFiles.map((file, index) => (
      <InlineStack gap="200" align="start" blockAlign="center" key={index}>
        <div className="maxtextwidth">
          {file.name}{" "}
          <Text variant="bodySm" as="p">
            {file.size} bytes
          </Text>
        </div>
      </InlineStack>
    ));
  const uploadedKeysRef = useRef([]);
  const uploadImages = async (acceptedFiles) => {
    setIsImageUploading(true);
    try {
      const uploadedURLs = [];
      const newKeys = [];
      for (const file of acceptedFiles) {
        const form_data = new FormData();
        form_data.append("entity_type", "brand_benchmark_screenshot");
        form_data.append("image", file);

        const result = await axios.post(`/s3/save-file`, form_data);
        newKeys.push(result.data.data.file_key);
        const previewUrl = validImageTypes.includes(file.type)
          ? window.URL.createObjectURL(file)
          : "https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png";
        uploadedURLs.push(previewUrl);
      }
      uploadedKeysRef.current = [...uploadedKeysRef.current, ...newKeys];
      setItem((prev) => ({
        ...prev,
        images: uploadedURLs,
      }));

      setIsImageUploading(false);
    } catch (error) {
      console.error("Upload error:", error);
      setIsImageUploading(false);
    }
  };

  return (
    <Page
      title={`${mode === "new" ? "Add" : "Edit"} Item`}
      backAction={{
        onAction: () => navigate(`/admin/brand-benchmark`),
      }}
    >
      {isSaving && <Loading />}

      <Card sectioned>
        <FormLayout>
          {/* <Select
            label="Country"
            options={optionsCountries}
            onChange={(value) => handleChange("country", value)}
            value={item.country}
            placeholder="Please select"
            error={errors.country}
          /> */}
          {/* <Select
            label="Competitor"
            options={competitorsOptions}
            value={item.competitor}
            onChange={(value) => handleChange("competitor", value)}
            placeholder="Please select"
            error={errors.competitor}
          /> */}
          <Text>Competitor</Text>
          <Select2
            options={competitorsOptions}
            onChange={(value) => handleChange("competitor", value)}
            value={item.competitor}
            placeholder="Please select"
            styles={{
              // Fixes the overlapping problem of the compont
              menu: (provided) => ({ ...provided, zIndex: 9999 }),
            }}
          />
          {/* <Select
            label="Category"
            options={categoryOptions}
            value={item.category}
            onChange={(value) => handleChange("category", value)}
            placeholder="Please select"
            error={errors.category}
          /> */}
          <Select
            label="Criteria"
            options={criteriaOptions}
            value={item.criteria}
            onChange={(value) => handleChange("criteria", value)}
            placeholder="Please select"
            error={errors.criteria}
            labelAction={{
              content: "Edit Criterias", // 👈 The text shown on the right of the label
              onAction: () => navigate("/admin/brand-benchmark/edit/criterias"), // 👈 Button action
              icon: EditIcon,
            }}
          />

          {/* <TextField
            label="Platform Name"
            value={item.platform_name}
            onChange={(value) => handleChange("platform_name", value)}
            error={errors.platform_name}
          /> */}

          <Text>Description</Text>
          <JoditEditor
            value={item.description}
            onChange={(value) => handleChange("description", value)}
            config={editorConfig}
          />
          {errors.description && <InlineError message={errors.description} />}

          {/* <Text as="p" fontWeight="regular">
            Platform Logo
          </Text>
          <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
            <div style={{ float: "left" }}>
              <Thumbnail size="large" source={item.platform_logo || ""} />
            </div>
            <div style={{ width: "100%", marginLeft: "10px" }}>
              <DropZone
                onDrop={handleDropZone}
                onDropAccepted={sendImage}
                allowMultiple={false}
              >
                {uploadedFiles}
                {fileUpload}
              </DropZone>
            </div>
          </div>
          <InlineError message={fileError} /> */}

          {mode === "new" ? (
            <FormLayout>
              <Text as="p" fontWeight="regular">
                Images
              </Text>

              <div style={{ width: "100%" }}>
                {/* Thumbnails grid */}
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "10px",
                  }}
                >
                  {item.images?.length > 0 &&
                    item.images.map((image, index) => (
                      <Thumbnail
                        key={index}
                        size="small"
                        source={image || ""}
                        alt={`image-${index}`}
                      />
                    ))}
                </div>

                <div style={{ width: "100%", marginLeft: "10px" }}>
                  <DropZone
                    onDrop={handleImageDrop}
                    onDropAccepted={uploadImages}
                    allowMultiple
                  >
                    {uploadedImageFiles}
                    {imageFileUpload}
                  </DropZone>
                </div>
              </div>

              <InlineError message={errors.images} />
            </FormLayout>
          ) : null}

          {mode === "edit" ? (
            <Button
              fullWidth
              icon={ImagesIcon}
              variant="primary"
              onClick={() => navigate(`/admin/brand-benchmark/${id}/images`)}
            >
              Images
            </Button>
          ) : null}

          {/* <Box paddingInline="400" paddingBlockEnd="400">
            <InlineGrid
              columns={{ xs: 4, sm: 6, md: 8, lg: 10 }}
              gap="200"
              alignItems="center"
            >
              {images.map((src, i) => (
                <Box
                  key={i}
                  as="button"
                  onClick={() => console.log("clicked", i)}
                  padding="0"
                  background="bg-surface"
                  borderWidth="025"
                  borderColor="border"
                  borderRadius="100"
                  overflowWrap="anywhere"
                  style={{
                    display: "grid",
                    placeItems: "center",
                    width: 64,
                    height: 64,
                  }}
                >
                  <Thumbnail alt={`thumb-${i}`} source={src} size="small" />
                </Box>
              ))}
            </InlineGrid>
          </Box> */}
        </FormLayout>
      </Card>

      <PageActions
        primaryAction={{
          content: "Save",
          onAction: handleSave,
          loading: isSaving,
        }}
        secondaryActions={
          mode === "edit" && (
            <Button
              variant="primary"
              loading={isSaving}
              tone="critical"
              onClick={() => setActiveDelete(true)}
            >
              Delete
            </Button>
          )
        }
      />

      <Modal
        open={activeDelete}
        onClose={toggleDeleteModal}
        title="Delete Item"
        primaryAction={{
          content: "Yes",
          onAction: handleYesDelete,
          loading: isSaving,
        }}
        secondaryActions={[
          {
            content: "No",
            onAction: toggleDeleteModal,
            loading: isSaving,
          },
        ]}
      >
        <Modal.Section>
          <Text>Are you sure you want to delete this item?</Text>
        </Modal.Section>
      </Modal>

      {toastMarkup}
      {isImageUploading ? <LoadingScreen /> : null}
    </Page>
  );

  function handleSave() {
    if (!validateFields()) {
      return;
    }
    setIsSaving(true);
    const formData = new FormData();
    mode === "edit" && formData.append("_method", "PATCH");
    // formData.append("platform_name", item.platform_name);
    formData.append("description", item.description);
    formData.append("criteria_id", item.criteria);
    formData.append("category_id", item.category);
    // formData.append("country", item.country);
    formData.append("competitor_id", item.competitor.value);
    // imageKey.current && formData.append("platform_logo", imageKey.current);
    uploadedKeysRef?.current?.length &&
      uploadedKeysRef.current.forEach((selected, index) =>
        formData.append(`images[${index}]`, selected)
      );
    axios
      .post(
        mode === "new" ? "/brand-benchmarks" : `/brand-benchmarks/${id}`,
        formData
      )
      .then(() => {
        mode === "new" ? navigate("/admin/brand-benchmark") : toggleActive();
      })
      .catch((error) => {
        console.log(error.response);
        setIsSaving(false);
        if (error.response && error.response.data?.message) {
          const serverErrors = error.response.data.message;
          setError(serverErrors);
        } else {
          console.error(error);
        }
      })
      .finally(() => setIsSaving(false));
  }
};

export default AddEditPlatform;
